/**
 * The projects app's API client.
 *
 * Same shape as the pager's and the vault's: the base URL is runtime
 * configuration (`config.js`, written per instance) rather than baked into the
 * bundle, and the token is the PKCE session this app holds for its own Cognito
 * client. Permission is decided by the client a token was signed for, so the
 * app runs on its own token and nobody else's.
 */

let tokenSource: (() => string | null) | null = null;
let onUnauthorized: (() => void) | null = null;

export function onUnauthorizedCall(handler: () => void) {
  onUnauthorized = handler;
}

export function useTokenFrom(source: () => string | null) {
  tokenSource = source;
}

function base(): string {
  const configured = (window as unknown as {
    KERNEL_CONFIG?: { projectsApi?: string };
  }).KERNEL_CONFIG?.projectsApi;
  if (!configured) {
    throw new Error('no projectsApi in KERNEL_CONFIG — config.js is missing or stale');
  }
  return configured.replace(/\/$/, '');
}

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, body: any) {
    super(body?.error || body?.detail || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

let reauthenticating = false;

/** A 401 means sign in again — most often because a reinstall built a new
 *  Cognito client and every held session is signed for the old one. Guarded
 *  so a genuinely unauthorised call cannot loop the browser. */
function reauthenticate() {
  if (reauthenticating || !onUnauthorized) return;
  reauthenticating = true;
  try { sessionStorage.removeItem('kernel.token'); } catch { /* private mode */ }
  onUnauthorized();
}

async function call(method: string, path: string, body?: unknown) {
  const token = tokenSource?.() ?? null;
  const response = await fetch(`${base()}${path}`, {
    method,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await response.text();
  let parsed: any;
  try { parsed = text ? JSON.parse(text) : undefined; } catch { parsed = undefined; }
  if (response.status === 401) reauthenticate();
  if (!response.ok) throw new ApiError(response.status, parsed ?? {});
  return parsed;
}

const enc = encodeURIComponent;

export const api = {
  people: () => call('GET', '/projects/people'),
  search: (q: string) => call('GET', `/projects/search?q=${enc(q)}`),

  projects: (opts?: { archived?: boolean }) => call('GET', `/projects/projects${opts && opts.archived ? '?archived=1' : ''}`),
  createProject: (body: unknown) => call('POST', '/projects/projects', body),
  project: (id: string) => call('GET', `/projects/projects/${enc(id)}`),
  updateProject: (id: string, body: unknown) => call('PUT', `/projects/projects/${enc(id)}`, body),
  deleteProject: (id: string) => call('DELETE', `/projects/projects/${enc(id)}`),
  board: (id: string) => call('GET', `/projects/projects/${enc(id)}/board`),

  createSection: (id: string, body: unknown) => call('POST', `/projects/projects/${enc(id)}/sections`, body),
  updateSection: (id: string, sectionId: string, body: unknown) =>
    call('PUT', `/projects/projects/${enc(id)}/sections/${enc(sectionId)}`, body),
  deleteSection: (id: string, sectionId: string) =>
    call('DELETE', `/projects/projects/${enc(id)}/sections/${enc(sectionId)}`),

  createTask: (id: string, body: unknown) => call('POST', `/projects/projects/${enc(id)}/tasks`, body),
  updateTask: (id: string, taskId: string, body: unknown) =>
    call('PUT', `/projects/projects/${enc(id)}/tasks/${enc(taskId)}`, body),
  deleteTask: (id: string, taskId: string) =>
    call('DELETE', `/projects/projects/${enc(id)}/tasks/${enc(taskId)}`),

  epics: (id: string) => call('GET', `/projects/projects/${enc(id)}/epics`),
  createEpic: (id: string, body: unknown) => call('POST', `/projects/projects/${enc(id)}/epics`, body),
  updateEpic: (id: string, epicId: string, body: unknown) =>
    call('PUT', `/projects/projects/${enc(id)}/epics/${enc(epicId)}`, body),

  comments: (taskId: string) => call('GET', `/projects/tasks/${enc(taskId)}/comments`),
  addComment: (taskId: string, body: unknown) => call('POST', `/projects/tasks/${enc(taskId)}/comments`, body),

  tags: (id: string) => call('GET', `/projects/projects/${enc(id)}/tags`),
  createTag: (id: string, body: unknown) => call('POST', `/projects/projects/${enc(id)}/tags`, body),
  deleteTag: (id: string, tagId: string) => call('DELETE', `/projects/projects/${enc(id)}/tags/${enc(tagId)}`),

  followers: (taskId: string) => call('GET', `/projects/tasks/${enc(taskId)}/followers`),
  // The server follows *the caller* as their kernel Person id; it only needs
  // the project so a followed task can be found for the My tasks list.
  follow: (taskId: string, projectId: string) =>
    call('POST', `/projects/tasks/${enc(taskId)}/followers`, { projectId }),
  unfollow: (taskId: string, userId: string) =>
    call('DELETE', `/projects/tasks/${enc(taskId)}/followers/${enc(userId)}`),

  // Every task assigned to me, across projects. The server resolves "me" to
  // the kernel Person whose emails include mine, since an assignee is a
  // Person id and not a Cognito sub.
  myTasks: () => call('GET', '/projects/me/tasks'),
};

export const PRIORITIES = ['low', 'med', 'high'] as const;
export const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'] as const;
